import {
  Box,
  Breadcrumbs,
  Button,
  Container,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Chip,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Tabs,
  Tab,
} from "@mui/material";
import PropTypes from "prop-types";
import PurchaseSourceSelect from "./PurchaseSourceSelect";
import { Link } from "react-router-dom";
import React, { useEffect, useState } from "react";
import { useTheme, useMediaQuery } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import api from "../axiosInstance";
import NavBar from "./NavBar";
import DownloadIcon from "@mui/icons-material/Download";
import RefreshIcon from "@mui/icons-material/Refresh";
import InfoIcon from "@mui/icons-material/Info";
import { toast } from "react-toastify";
import { isMarketingUser } from "../auth";
import { useNavigate } from "react-router";
import CancelIcon from "@mui/icons-material/Cancel";
import { Padding } from "@mui/icons-material";
import FilterBar from "./Reusable/FilterBar";
// Tabs css and aliginments code
function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: { xs: 1, md: 0 } }}>{children}</Box>}
    </div>
  );
}

CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

// Helper function to get date ranges
const getDateRange = (range) => {
  const today = new Date();
  const todayStr = today.toISOString().split("T")[0];

  switch (range) {
    case "today":
      return { startDate: todayStr, endDate: todayStr };
    case "yesterday":
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      const yesterdayStr = yesterday.toISOString().split("T")[0];
      return { startDate: yesterdayStr, endDate: yesterdayStr };
    case "last2days":
      const twoDaysAgo = new Date(today);
      twoDaysAgo.setDate(twoDaysAgo.getDate() - 2);
      const twoDaysAgoStr = twoDaysAgo.toISOString().split("T")[0];
      return { startDate: twoDaysAgoStr, endDate: todayStr };
    case "last7days":
      const weekAgo = new Date(today);
      weekAgo.setDate(weekAgo.getDate() - 7);
      const weekAgoStr = weekAgo.toISOString().split("T")[0];
      return { startDate: weekAgoStr, endDate: todayStr };
    case "last30days":
      const monthAgo = new Date(today);
      monthAgo.setDate(monthAgo.getDate() - 30);
      const monthAgoStr = monthAgo.toISOString().split("T")[0];
      return { startDate: monthAgoStr, endDate: todayStr };
    case "last2months":
      const twoMonthsAgo = new Date(today);
      twoMonthsAgo.setMonth(twoMonthsAgo.getMonth() - 2);
      const twoMonthsAgoStr = twoMonthsAgo.toISOString().split("T")[0];
      return { startDate: twoMonthsAgoStr, endDate: todayStr };
    case "last3months":
      const threeMonthsAgo = new Date(today);
      threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
      const threeMonthsAgoStr = threeMonthsAgo.toISOString().split("T")[0];
      return { startDate: threeMonthsAgoStr, endDate: todayStr };
    case "last6months":
      const sixMonthsAgo = new Date(today);
      sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
      const sixMonthsAgoStr = sixMonthsAgo.toISOString().split("T")[0];
      return { startDate: sixMonthsAgoStr, endDate: todayStr };
    case "lastyear":
      const yearAgo = new Date(today);
      yearAgo.setFullYear(yearAgo.getFullYear() - 1);
      const yearAgoStr = yearAgo.toISOString().split("T")[0];
      return { startDate: yearAgoStr, endDate: todayStr };
    default:
      return { startDate: "", endDate: "" };
  }
};

//  Reusable custom hook
// withSource: the report takes the purchase source filter (All / Website /
// App), sent as &source= -- the three purchase reports.
function useDownloadFormik({
  endpoint,
  downloadFileName,
  universityId,
  courseId,
  semesterId,
  withSource = false,
}) {
  const [selectedRange, setSelectedRange] = useState("custom");

  const formik = useFormik({
    initialValues: {
      startDate: "",
      endDate: "",
      source: "ALL",
    },
    validationSchema: Yup.object({
      startDate: Yup.string().required("From Date is required"),
      endDate: Yup.string()
        .required("To Date is required")
        .test(
          "is-greater",
          "To Date must be after or same as From Date",
          function (value) {
            const { startDate } = this.parent;
            return (
              !startDate || !value || new Date(value) >= new Date(startDate)
            );
          },
        ),
    }),
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      const sourceQuery = withSource ? `&source=${values.source}` : "";
      try {
        // Try different approaches based on the API requirements
        console.log("Download request:", {
          endpoint: `${endpoint}?startDate=${values.startDate}&endDate=${values.endDate}${sourceQuery}`,
          method: "POST",
          body: {},
          responseType: "blob",
        });

        // First try: POST with query parameters (as per curl request)
        let response;
        try {
          response = await api.post(
            `${endpoint}?startDate=${values.startDate}&endDate=${values.endDate}${universityId ? `&universityId=${universityId}` : ""}${courseId ? `&courseId=${courseId}` : ""}${semesterId ? `&semesterId=${semesterId}` : ""}${sourceQuery}`,
            {},
            {
              responseType: "blob",
              headers: { "Content-Type": "application/json" },
            },
          );
        } catch (firstError) {
          console.log(
            "First attempt failed, trying alternative approach...",
            firstError,
          );
          response = await api.post(
            endpoint,
            {},
            {
              params: {
                startDate: values.startDate,
                endDate: values.endDate,
                universityId: universityId || undefined,
                courseId: courseId || undefined,
                semesterId: semesterId || undefined,
                ...(withSource ? { source: values.source } : {}),
              },
              responseType: "blob",
              headers: { "Content-Type": "application/json" },
            },
          );
        }

        const blob = new Blob([response.data]);
        const url = window.URL.createObjectURL(blob);

        const a = document.createElement("a");
        a.href = url;
        a.download = `${downloadFileName}_${values.startDate}_to_${values.endDate}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        toast.success(`${downloadFileName} downloaded successfully!`);
        resetForm();
        handleRangeSelect();
      } catch (error) {
        console.error("Download Error:", error);
        console.error("Error response:", error.response);

        // Show user-friendly error message
        let errorMessage = "Unknown error occurred";

        if (error.response) {
          // Server responded with error status
          errorMessage =
            error.response.data?.message ||
            error.response.data?.error ||
            `HTTP ${error.response.status}: ${error.response.statusText}`;
        } else if (error.request) {
          // Request was made but no response received
          errorMessage =
            "No response from server. Please check your connection.";
        } else {
          // Something else happened
          errorMessage = error.message;
        }

        toast.error(`Download failed: ${errorMessage}`);
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleRangeSelect = (range) => {
    setSelectedRange(range);
    if (range !== "custom") {
      const dates = getDateRange(range);
      formik.setFieldValue("startDate", dates.startDate);
      formik.setFieldValue("endDate", dates.endDate);
    }
  };

  return {
    formik,
    selectedRange,
    setSelectedRange,
    handleRangeSelect,
  };
}

// Date Range Selector Component
const DateRangeSelector = ({ selectedRange, handleRangeSelect, formik }) => {
  const rangeOptions = [
    { value: "today", label: "Today" },
    { value: "yesterday", label: "Yesterday" },
    { value: "last2days", label: "Last 2 Days" },
    { value: "last7days", label: "Last 7 Days" },
    { value: "last30days", label: "Last 30 Days" },
    { value: "last2months", label: "Last 2 Months" },
    { value: "last3months", label: "Last 3 Months" },
    { value: "last6months", label: "Last 6 Months" },
    { value: "lastyear", label: "Last Year" },
    { value: "custom", label: "Custom Range" },
  ];

  return (
    <Box sx={{ mb: 3 }}>
      <Typography
        variant="subtitle2"
        sx={{ mb: 2, fontWeight: 600, color: "text.primary" }}
      >
        Quick Date Range
      </Typography>

      <ToggleButtonGroup
        value={selectedRange}
        exclusive
        onChange={(event, newRange) => {
          if (newRange !== null) {
            handleRangeSelect(newRange);
          }
        }}
        size="small"
        sx={{
          flexWrap: "wrap",
          gap: 0.5,
          mb: 2,
          "& .MuiToggleButton-root": {
            fontSize: "0.75rem",
            px: 1.5,
            py: 0.5,
            minWidth: "auto",
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 500,
            border: "1px solid",
            borderColor: "divider",
            "&:hover": {
              backgroundColor: "action.hover",
            },
            "&.Mui-selected": {
              backgroundColor: "primary.main",
              color: "primary.contrastText",
              "&:hover": {
                backgroundColor: "primary.dark",
              },
            },
          },
        }}
      >
        {rangeOptions.map((option) => (
          <ToggleButton key={option.value} value={option.value}>
            {option.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      {selectedRange === "custom" && (
        <Box
          sx={{
            mt: 2,
            display: "flex",
            gap: 2,
            flexWrap: "wrap",
            alignItems: "flex-start",
            "& > *": {
              flex: "1 1 200px",
              minWidth: "150px",
            },
          }}
        >
          <TextField
            label="From Date"
            type="date"
            name="startDate"
            value={formik.values.startDate}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.startDate && Boolean(formik.errors.startDate)}
            helperText={formik.touched.startDate && formik.errors.startDate}
            InputLabelProps={{ shrink: true }}
            size="small"
            variant="outlined"
            fullWidth
          />
          <TextField
            label="To Date"
            type="date"
            name="endDate"
            value={formik.values.endDate}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.endDate && Boolean(formik.errors.endDate)}
            helperText={formik.touched.endDate && formik.errors.endDate}
            InputLabelProps={{ shrink: true }}
            size="small"
            variant="outlined"
            fullWidth
          />
        </Box>
      )}
    </Box>
  );
};

function DBDataNew() {
  const [open, setOpen] = React.useState(false);
  const [dialogType, setDialogType] = useState("");
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [value, setValue] = useState(0);
  const [universityId, setUniversityId] = useState("");
  const [courseId, setCourseId] = useState("");
  const [semesterId, setSemesterId] = useState("");

  // Tabs chnages values
  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  // useEffect(() => {
  //   if (!isMarketingUser()) {
  //     toast.error("You do not have access to DB Data.");
  //     navigate("/booklist");
  //   }
  // }, [navigate]);

  const handleClickOpen = (type) => {
    setDialogType(type);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setDialogType("");
  };
  const feedbackData = useDownloadFormik({
    endpoint: "/api/reports/feedbackReports",
    downloadFileName: "feedback_reports",
  });
  const booksPurchaseReport = useDownloadFormik({
    endpoint: "/api/reports/bookPurchaseReports",
    downloadFileName: "books_purchase_reports",
    universityId,
    courseId,
    semesterId,
    withSource: true,
  });
  const courseNotificationsReport = useDownloadFormik({
    endpoint: "/api/reports/courseNotificationReports",
    downloadFileName: "Course_Notifications_reports",
  });
  const universityNotificationsReport = useDownloadFormik({
    endpoint: "/api/reports/universityNotificationsReports",
    downloadFileName: "University_Notifications_reports",
  });
  const DailyPurchaseReport = useDownloadFormik({
    endpoint: "/api/reports/DailyPurchaseReports",
    downloadFileName: "daily_purchase_reports",
    withSource: true,
  });

  const purchaseData = useDownloadFormik({
    endpoint: "/api/reports/purchasesReports",
    downloadFileName: "purchases_reports",
    withSource: true,
  });
  const iospuchasesData = useDownloadFormik({
    endpoint: "/api/reports/appleOrders",
    downloadFileName: "Ios_purchases_reports",
  });
  const purchaseDataUnique = useDownloadFormik({
    endpoint: "/api/reports/purchasesReports2",
    downloadFileName: "Unique_purchases_reports",
    withSource: true,
  });
  const UniqueUserpurchaseData = useDownloadFormik({
    endpoint: "/api/reports/uniquePurchaseReports",
    downloadFileName: "Unique_user_purchases_reports",
    withSource: true,
  });
  const appDataData = useDownloadFormik({
    endpoint: "/api/reports/appDownloadsReports",
    downloadFileName: "APP_Download_reports",
  });

  const couponUsageData = useDownloadFormik({
    endpoint: "/api/reports/couponReports",
    downloadFileName: "coupon_usage_reports",
  });
  const notificationsUsageData = useDownloadFormik({
    endpoint: "/api/reports/bookNotificationReports",
    downloadFileName: "Book_Notifications_reports",
  });

  // Content for each type of dialog box
  const getDialogContent = () => {
    switch (dialogType) {
      case "feedback":
        return (
          <div>
            <p>
              This feature is used to see all the feedback given by users within
              a specific date range. For each feedback entry, it shows:
            </p>
            <ul>
              <li>The date the feedback was given (in Indian time)</li>
              <li>The user’s name and contact number</li>
              <li>Their university, course, semester, and college</li>
              <li>The book and section they gave feedback on</li>
              <li>The actual feedback text and the rating they provided</li>
            </ul>
          </div>
        );
      case "booksPurchase":
        return (
          <div>
            <p>
              This feature is used to view all book purchase records within a
              specific date range. For each purchase entry, it shows:
            </p>
            <ul>
              <li>The date of purchase (in Indian time)</li>
              <li>The user’s name and contact number</li>
              <li>Their university, course, semester, and college</li>
              <li>The book name and quantity purchased</li>
              <li>The total amount paid and payment status</li>
            </ul>
          </div>
        );
      case "courseNotifications":
        return (
          <div>
            <p>
              This feature is used to view all course notification records
              within a specific date range. For each notification entry, it
              shows:
            </p>
            <ul>
              <li>The date of notification (in Indian time)</li>
              <li>The user’s name and contact number</li>
              <li>Their university, course, semester, and college</li>
              <li>The course name and notification content</li>
              <li>The notification status and delivery details</li>
            </ul>
          </div>
        );
      case "universityNotifications":
        return (
          <div>
            <p>
              This feature is used to view all university notification records
              within a specific date range. For each notification entry, it
              shows:
            </p>
            <ul>
              <li>The date of notification (in Indian time)</li>
              <li>The user’s name and contact number</li>
              <li>Their university, course, semester, and college</li>
              <li>The course name and notification content</li>
              <li>The notification status and delivery details</li>
            </ul>
          </div>
        );
      case "dailyPurchase":
        return (
          <div>
            <p>
              This feature is used to view the complete daily report data within
              a selected date range. For each report entry, it shows:
            </p>

            <ul>
              <li>The report date and time (in Indian time)</li>
              <li>Total number of records generated</li>
              <li>User activity and transaction details</li>
              <li>Total sales, purchases, or collections</li>
              <li>Payment status and transaction summaries</li>
              <li>System-generated daily performance data</li>
            </ul>
          </div>
        );
      case "purchase":
        return (
          <div>
            <p>
              This feature is used to track purchases made by users in the app
              during a specific date range that the user enters. It looks at the{" "}
              <strong>sales_reporting</strong> table where each payment is
              recorded.
            </p>

            <p>For every purchase, it pulls:</p>
            <ul>
              <li>User’s phone number and name</li>
              <li>University, course, semester, and college</li>
              <li>Book purchased and the amount paid (in rupees)</li>
              <li>Purchase date (converted to Indian Standard Time)</li>
            </ul>

            <p>
              It only includes purchases where the payment status is marked as{" "}
              <strong>“captured”</strong> (i.e., successful transactions).
            </p>

            <p>
              <strong>Criteria used for fetching this data:</strong>
            </p>
            <ul>
              <li>
                <strong>Purchase Date:</strong> Only purchases made within the
                given date range are included.
              </li>
              <li>
                <strong>Payment Status:</strong> Only successful transactions (
                <code>status = 'captured'</code>) are counted.
              </li>
              <li>
                <strong>Book Details:</strong> The book purchased is matched
                using the <code>book_id</code>.
              </li>
              <li>
                <strong>User Details:</strong> Contextual info (university,
                course, semester, college) is pulled from linked tables.
              </li>
            </ul>
          </div>
        );
      case "Unique_user_Purchase":
        return (
          <div>
            <p>
              This feature is used to track purchases made by users in the app
              during a specific date range that the user enters. It looks at the{" "}
              <strong>sales_reporting</strong> table where each payment is
              recorded.
            </p>

            <p>For every purchase, it pulls:</p>
            <ul>
              <li>User’s phone number and name</li>
              <li>University, course, semester, and college</li>
              <li>Book purchased and the amount paid (in rupees)</li>
              <li>Purchase date (converted to Indian Standard Time)</li>
            </ul>

            <p>
              It only includes purchases where the payment status is marked as{" "}
              <strong>“captured”</strong> (i.e., successful transactions).
            </p>

            <p>
              <strong>Criteria used for fetching this data:</strong>
            </p>
            <ul>
              <li>
                <strong>Purchase Date:</strong> Only purchases made within the
                given date range are included.
              </li>
              <li>
                <strong>Payment Status:</strong> Only successful transactions (
                <code>status = 'captured'</code>) are counted.
              </li>
              <li>
                <strong>Book Details:</strong> The book purchased is matched
                using the <code>book_id</code>.
              </li>
              <li>
                <strong>User Details:</strong> Contextual info (university,
                course, semester, college) is pulled from linked tables.
              </li>
            </ul>
          </div>
        );
      case "purchase_unique":
        return (
          <div>
            <p>
              This feature is used to track purchases made by users in the app
              during a specific date range that the user enters. It looks at the{" "}
              <strong>sales_reporting</strong> table where each payment is
              recorded.
            </p>

            <p>For every purchase, it pulls:</p>
            <ul>
              <li>User’s phone number and name</li>
              <li>University, course, semester, and college</li>
              <li>Book purchased and the amount paid (in rupees)</li>
              <li>Purchase date (converted to Indian Standard Time)</li>
            </ul>

            <p>
              It only includes purchases where the payment status is marked as{" "}
              <strong>“captured”</strong> (i.e., successful transactions).
            </p>

            <p>
              <strong>Criteria used for fetching this data:</strong>
            </p>
            <ul>
              <li>
                <strong>Purchase Date:</strong> Only purchases made within the
                given date range are included.
              </li>
              <li>
                <strong>Payment Status:</strong> Only successful transactions (
                <code>status = 'captured'</code>) are counted.
              </li>
              <li>
                <strong>Book Details:</strong> The book purchased is matched
                using the <code>book_id</code>.
              </li>
              <li>
                <strong>User Details:</strong> Contextual info (university,
                course, semester, college) is pulled from linked tables.
              </li>
            </ul>
          </div>
        );
      case "ios_purchases":
        return (
          <div>
            <p>
              This feature is used to track purchases made by users in the app
              during a specific date range that the user enters. It looks at the{" "}
              <strong>sales_reporting</strong> table where each payment is
              recorded.
            </p>

            <p>For every purchase, it pulls:</p>
            <ul>
              <li>User’s phone number and name</li>
              <li>University, course, semester, and college</li>
              <li>Book purchased and the amount paid (in rupees)</li>
              <li>Purchase date (converted to Indian Standard Time)</li>
            </ul>

            <p>
              It only includes purchases where the payment status is marked as{" "}
              <strong>“captured”</strong> (i.e., successful transactions).
            </p>

            <p>
              <strong>Criteria used for fetching this data:</strong>
            </p>
            <ul>
              <li>
                <strong>Purchase Date:</strong> Only purchases made within the
                given date range are included.
              </li>
              <li>
                <strong>Payment Status:</strong> Only successful transactions (
                <code>status = 'captured'</code>) are counted.
              </li>
              <li>
                <strong>Book Details:</strong> The book purchased is matched
                using the <code>book_id</code>.
              </li>
              <li>
                <strong>User Details:</strong> Contextual info (university,
                course, semester, college) is pulled from linked tables.
              </li>
            </ul>
          </div>
        );
      case "download":
        return (
          <div>
            <p>
              This query is used to find out how many people downloaded and
              registered on the app within a specific date range. It looks at
              the <strong>users</strong> table where every new app download and
              registration is recorded. From each record, it pulls useful
              details like:
            </p>
            <ul>
              <li>User’s phone number and name</li>
              <li>University, course, semester, and college name</li>
              <li>Registration date (converted to Indian Standard Time)</li>
            </ul>
            <p>The results are shown in order of the user’s phone number.</p>
            <p>
              <strong>Criteria used for fetching data:</strong>
            </p>
            <ul>
              <li>
                <strong>Account creation date (creation_date):</strong> Only
                users who created their account during the selected date range
                are included.
              </li>
              <li>
                <strong>Linked details:</strong> Information is enriched by
                joining with university, course, and college records connected
                to each user.
              </li>
            </ul>
          </div>
        );
      case "coupon":
        return (
          <div>
            <p>
              Export coupon usage data within a specific date range. This shows
              redemptions and related metadata for analysis.
            </p>
            <ul>
              <li>Coupon code, type, and discount details</li>
              <li>User identifier and book/course context where applicable</li>
              <li>Redemption date/time (in Indian Standard Time)</li>
              <li>Associated order or transaction reference (if any)</li>
            </ul>
          </div>
        );
      case "Notifications":
        return (
          <div>
            <p>
              This query is used to find out how many people downloaded and
              registered on the app within a specific date range. It looks at
              the <strong>users</strong> table where every new app download and
              registration is recorded. From each record, it pulls useful
              details like:
            </p>
            <ul>
              <li>User’s phone number and name</li>
              <li>University, course, semester, and college name</li>
              <li>Registration date (converted to Indian Standard Time)</li>
            </ul>
            <p>The results are shown in order of the user’s phone number.</p>
            <p>
              <strong>Criteria used for fetching data:</strong>
            </p>
            <ul>
              <li>
                <strong>Account creation date (creation_date):</strong> Only
                users who created their account during the selected date range
                are included.
              </li>
              <li>
                <strong>Linked details:</strong> Information is enriched by
                joining with university, course, and college records connected
                to each user.
              </li>
            </ul>
          </div>
        );
      default:
        return "";
    }
  };
  return (
    <>
      <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
        {/* Header Section */}
        {/* <Card sx={{ mb: 3, boxShadow: 2 }}>
          <CardContent> */}

        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          Export various types of data from the database for analysis and
          reporting purposes.
        </Typography>
        {/* </CardContent>
        </Card> */}

        <Container maxWidth="lg" sx={{ px: { xs: 1, sm: 2 } }}>
          <Card sx={{ mb: 3, boxShadow: 1 }}>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 2,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, color: "primary.main" }}
                  >
                    Daily Purchase Reports
                  </Typography>
                  <Chip
                    label="Daily Purchased"
                    color="warning"
                    size="small"
                    variant="outlined"
                  />
                  <IconButton
                    size="small"
                    onClick={() => handleClickOpen("dailyPurchase")}
                    sx={{ color: "text.secondary" }}
                  >
                    <InfoIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Export Daily purchase data within a specific date range for
                analysis and reporting.
              </Typography>

              <DateRangeSelector
                selectedRange={DailyPurchaseReport.selectedRange}
                handleRangeSelect={DailyPurchaseReport.handleRangeSelect}
                formik={DailyPurchaseReport.formik}
              />

              <Box
                component="form"
                onSubmit={DailyPurchaseReport.formik.handleSubmit}
                sx={{
                  display: "flex",
                  gap: 2,
                  alignItems: "center",
                  flexWrap: "wrap",
                  mt: 1,
                  flexDirection: { xs: "column", sm: "row" },
                }}
              >
                <PurchaseSourceSelect formik={DailyPurchaseReport.formik} />
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={
                    DailyPurchaseReport.formik.isSubmitting ? (
                      <CircularProgress size={20} />
                    ) : (
                      <DownloadIcon />
                    )
                  }
                  disabled={DailyPurchaseReport.formik.isSubmitting}
                  size={isMobile ? "small" : "medium"}
                  sx={{
                    minWidth: { xs: "100%", sm: 140 },
                    height: 56,
                    width: { xs: "100%", sm: "auto" },
                  }}
                >
                  {DailyPurchaseReport.formik.isSubmitting
                    ? "Processing..."
                    : "Download"}
                </Button>
              </Box>
            </CardContent>
          </Card>
          <Card sx={{ mb: 3, boxShadow: 1 }}>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 2,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, color: "primary.main" }}
                  >
                    Books Purchase Reports
                  </Typography>
                  <Chip
                    label="Books Purchased"
                    color="info"
                    size="small"
                    variant="outlined"
                  />
                  <IconButton
                    size="small"
                    onClick={() => handleClickOpen("booksPurchase")}
                    sx={{ color: "text.secondary" }}
                  >
                    <InfoIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Export book purchase data within a specific date range for
                analysis and reporting.
              </Typography>

              <DateRangeSelector
                selectedRange={booksPurchaseReport.selectedRange}
                handleRangeSelect={booksPurchaseReport.handleRangeSelect}
                formik={booksPurchaseReport.formik}
              />
              <Box>
                <FilterBar
                  universityId={universityId}
                  courseId={courseId}
                  semesterId={semesterId}
                  setUniversityId={setUniversityId}
                  setCourseId={setCourseId}
                  setSemesterId={setSemesterId}
                  regulation={"Reports"}
                />
              </Box>
              <Box
                component="form"
                onSubmit={booksPurchaseReport.formik.handleSubmit}
                sx={{
                  display: "flex",
                  gap: 2,
                  alignItems: "center",
                  flexWrap: "wrap",
                  mt: 1,
                  flexDirection: { xs: "column", sm: "row" },
                }}
              >
                <PurchaseSourceSelect formik={booksPurchaseReport.formik} />
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={
                    booksPurchaseReport.formik.isSubmitting ? (
                      <CircularProgress size={20} />
                    ) : (
                      <DownloadIcon />
                    )
                  }
                  disabled={booksPurchaseReport.formik.isSubmitting}
                  size={isMobile ? "small" : "medium"}
                  sx={{
                    minWidth: { xs: "100%", sm: 140 },
                    height: 56,
                    width: { xs: "100%", sm: "auto" },
                  }}
                >
                  {booksPurchaseReport.formik.isSubmitting
                    ? "Processing..."
                    : "Download"}
                </Button>
              </Box>
            </CardContent>
          </Card>
          {/* Course notifications reports */}
          <Card sx={{ mb: 3, boxShadow: 1 }}>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 2,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, color: "primary.main" }}
                  >
                    Course Notifications Reports
                  </Typography>
                  <Chip
                    label="Course Notifications"
                    color="info"
                    size="small"
                    variant="outlined"
                  />
                  <IconButton
                    size="small"
                    onClick={() => handleClickOpen("courseNotifications")}
                    sx={{ color: "text.secondary" }}
                  >
                    <InfoIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Export course notification data within a specific date range for
                analysis and reporting.
              </Typography>

              <DateRangeSelector
                selectedRange={courseNotificationsReport.selectedRange}
                handleRangeSelect={courseNotificationsReport.handleRangeSelect}
                formik={courseNotificationsReport.formik}
              />

              <Box
                component="form"
                onSubmit={courseNotificationsReport.formik.handleSubmit}
                sx={{
                  display: "flex",
                  gap: 2,
                  alignItems: "center",
                  flexWrap: "wrap",
                  mt: 1,
                  flexDirection: { xs: "column", sm: "row" },
                }}
              >
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={
                    courseNotificationsReport.formik.isSubmitting ? (
                      <CircularProgress size={20} />
                    ) : (
                      <DownloadIcon />
                    )
                  }
                  disabled={courseNotificationsReport.formik.isSubmitting}
                  size={isMobile ? "small" : "medium"}
                  sx={{
                    minWidth: { xs: "100%", sm: 140 },
                    height: 56,
                    width: { xs: "100%", sm: "auto" },
                  }}
                >
                  {courseNotificationsReport.formik.isSubmitting
                    ? "Processing..."
                    : "Download"}
                </Button>
              </Box>
            </CardContent>
          </Card>

          {/* Course notifications reports */}
          <Card sx={{ mb: 3, boxShadow: 1 }}>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 2,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, color: "primary.main" }}
                  >
                    University Notifications Reports
                  </Typography>
                  <Chip
                    label="University Notifications"
                    color="info"
                    size="small"
                    variant="outlined"
                  />
                  <IconButton
                    size="small"
                    onClick={() => handleClickOpen("universityNotifications")}
                    sx={{ color: "text.secondary" }}
                  >
                    <InfoIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Export university notification data within a specific date range
                for analysis and reporting.
              </Typography>

              <DateRangeSelector
                selectedRange={universityNotificationsReport.selectedRange}
                handleRangeSelect={
                  universityNotificationsReport.handleRangeSelect
                }
                formik={universityNotificationsReport.formik}
              />

              <Box
                component="form"
                onSubmit={universityNotificationsReport.formik.handleSubmit}
                sx={{
                  display: "flex",
                  gap: 2,
                  alignItems: "center",
                  flexWrap: "wrap",
                  mt: 1,
                  flexDirection: { xs: "column", sm: "row" },
                }}
              >
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={
                    universityNotificationsReport.formik.isSubmitting ? (
                      <CircularProgress size={20} />
                    ) : (
                      <DownloadIcon />
                    )
                  }
                  disabled={universityNotificationsReport.formik.isSubmitting}
                  size={isMobile ? "small" : "medium"}
                  sx={{
                    minWidth: { xs: "100%", sm: 140 },
                    height: 56,
                    width: { xs: "100%", sm: "auto" },
                  }}
                >
                  {universityNotificationsReport.formik.isSubmitting
                    ? "Processing..."
                    : "Download"}
                </Button>
              </Box>
            </CardContent>
          </Card>
          {/* Feedback Data Card */}
          <Card sx={{ mb: 3, boxShadow: 1 }}>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 2,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, color: "primary.main" }}
                  >
                    App Feedback Reports
                  </Typography>
                  <Chip
                    label="User Feedback"
                    color="info"
                    size="small"
                    variant="outlined"
                  />
                  <IconButton
                    size="small"
                    onClick={() => handleClickOpen("feedback")}
                    sx={{ color: "text.secondary" }}
                  >
                    <InfoIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Export user feedback data within a specific date range for
                analysis and reporting.
              </Typography>

              <DateRangeSelector
                selectedRange={feedbackData.selectedRange}
                handleRangeSelect={feedbackData.handleRangeSelect}
                formik={feedbackData.formik}
              />

              <Box
                component="form"
                onSubmit={feedbackData.formik.handleSubmit}
                sx={{
                  display: "flex",
                  gap: 2,
                  alignItems: "center",
                  flexWrap: "wrap",
                  mt: 1,
                  flexDirection: { xs: "column", sm: "row" },
                }}
              >
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={
                    feedbackData.formik.isSubmitting ? (
                      <CircularProgress size={20} />
                    ) : (
                      <DownloadIcon />
                    )
                  }
                  disabled={feedbackData.formik.isSubmitting}
                  size={isMobile ? "small" : "medium"}
                  sx={{
                    minWidth: { xs: "100%", sm: 140 },
                    height: 56,
                    width: { xs: "100%", sm: "auto" },
                  }}
                >
                  {feedbackData.formik.isSubmitting
                    ? "Processing..."
                    : "Download"}
                </Button>
              </Box>
            </CardContent>
          </Card>
          {/* Purchase Data Card */}
          <Card sx={{ mb: 3, boxShadow: 1 }}>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 2,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, color: "primary.main" }}
                  >
                    App Purchase Reports
                  </Typography>
                  <Chip
                    label="Sales Data"
                    color="success"
                    size="small"
                    variant="outlined"
                  />
                  <IconButton
                    size="small"
                    onClick={() => handleClickOpen("purchase")}
                    sx={{ color: "text.secondary" }}
                  >
                    <InfoIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Export App Purchase Reports within a specific date range for
                revenue analysis.
              </Typography>

              <DateRangeSelector
                selectedRange={purchaseData.selectedRange}
                handleRangeSelect={purchaseData.handleRangeSelect}
                formik={purchaseData.formik}
              />

              <Box
                component="form"
                onSubmit={purchaseData.formik.handleSubmit}
                sx={{
                  display: "flex",
                  gap: 2,
                  alignItems: "center",
                  flexWrap: "wrap",
                  mt: 1,
                  flexDirection: { xs: "column", sm: "row" },
                }}
              >
                <PurchaseSourceSelect formik={purchaseData.formik} />
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={
                    purchaseData.formik.isSubmitting ? (
                      <CircularProgress size={20} />
                    ) : (
                      <DownloadIcon />
                    )
                  }
                  disabled={purchaseData.formik.isSubmitting}
                  size={isMobile ? "small" : "medium"}
                  sx={{
                    minWidth: { xs: "100%", sm: 140 },
                    height: 56,
                    width: { xs: "100%", sm: "auto" },
                  }}
                >
                  {purchaseData.formik.isSubmitting
                    ? "Processing..."
                    : "Download"}
                </Button>
              </Box>
            </CardContent>
          </Card>
          {/* Apply Purchase Data Card */}
          <Card sx={{ mb: 3, boxShadow: 1 }}>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 2,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, color: "primary.main" }}
                  >
                    IOS Purchases Reports
                  </Typography>
                  <Chip
                    label="IOS Purchases"
                    color="info"
                    size="small"
                    variant="outlined"
                  />
                  <IconButton
                    size="small"
                    onClick={() => handleClickOpen("ios_purchases")}
                    sx={{ color: "text.secondary" }}
                  >
                    <InfoIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Export IOS Purchases Reports within a specific date range for
                analysis and reporting.
              </Typography>

              <DateRangeSelector
                selectedRange={iospuchasesData.selectedRange}
                handleRangeSelect={iospuchasesData.handleRangeSelect}
                formik={iospuchasesData.formik}
              />

              <Box
                component="form"
                onSubmit={iospuchasesData.formik.handleSubmit}
                sx={{
                  display: "flex",
                  gap: 2,
                  alignItems: "center",
                  flexWrap: "wrap",
                  mt: 1,
                  flexDirection: { xs: "column", sm: "row" },
                }}
              >
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={
                    iospuchasesData.formik.isSubmitting ? (
                      <CircularProgress size={20} />
                    ) : (
                      <DownloadIcon />
                    )
                  }
                  disabled={iospuchasesData.formik.isSubmitting}
                  size={isMobile ? "small" : "medium"}
                  sx={{
                    minWidth: { xs: "100%", sm: 140 },
                    height: 56,
                    width: { xs: "100%", sm: "auto" },
                  }}
                >
                  {iospuchasesData.formik.isSubmitting
                    ? "Processing..."
                    : "Download"}
                </Button>
              </Box>
            </CardContent>
          </Card>
          {/* Purchase 2 Data  Card */}
          <Card sx={{ mb: 3, boxShadow: 1 }}>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 2,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, color: "primary.main" }}
                  >
                    App Unique Purchase Reports
                  </Typography>
                  <Chip
                    label="Unique Sales Data"
                    color="error"
                    size="small"
                    variant="outlined"
                  />
                  <IconButton
                    size="small"
                    onClick={() => handleClickOpen("purchase_unique")}
                    sx={{ color: "text.secondary" }}
                  >
                    <InfoIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Export Unique purchase and sales data within a specific date
                range for revenue analysis.
              </Typography>

              <DateRangeSelector
                selectedRange={purchaseDataUnique.selectedRange}
                handleRangeSelect={purchaseDataUnique.handleRangeSelect}
                formik={purchaseDataUnique.formik}
              />

              <Box
                component="form"
                onSubmit={purchaseDataUnique.formik.handleSubmit}
                sx={{
                  display: "flex",
                  gap: 2,
                  alignItems: "center",
                  flexWrap: "wrap",
                  mt: 1,
                  flexDirection: { xs: "column", sm: "row" },
                }}
              >
                <PurchaseSourceSelect formik={purchaseDataUnique.formik} />
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={
                    purchaseDataUnique.formik.isSubmitting ? (
                      <CircularProgress size={20} />
                    ) : (
                      <DownloadIcon />
                    )
                  }
                  disabled={purchaseDataUnique.formik.isSubmitting}
                  size={isMobile ? "small" : "medium"}
                  sx={{
                    minWidth: { xs: "100%", sm: 140 },
                    height: 56,
                    width: { xs: "100%", sm: "auto" },
                  }}
                >
                  {purchaseDataUnique.formik.isSubmitting
                    ? "Processing..."
                    : "Download"}
                </Button>
              </Box>
            </CardContent>
          </Card>
          {/* Unique user Purchase Data Card */}
          <Card sx={{ mb: 3, boxShadow: 1 }}>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 2,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, color: "primary.main" }}
                  >
                    Unique User Purchase Reports
                  </Typography>
                  <Chip
                    label="Unique UserPurchase"
                    color="warning"
                    size="small"
                    variant="outlined"
                  />
                  <IconButton
                    size="small"
                    onClick={() => handleClickOpen("Unique_user_Purchase")}
                    sx={{ color: "text.warning" }}
                  >
                    <InfoIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Export Unique User Purchase Reports within a specific date
                range.
              </Typography>

              <DateRangeSelector
                selectedRange={UniqueUserpurchaseData.selectedRange}
                handleRangeSelect={UniqueUserpurchaseData.handleRangeSelect}
                formik={UniqueUserpurchaseData.formik}
              />

              <Box
                component="form"
                onSubmit={UniqueUserpurchaseData.formik.handleSubmit}
                sx={{
                  display: "flex",
                  gap: 2,
                  alignItems: "center",
                  flexWrap: "wrap",
                  mt: 1,
                  flexDirection: { xs: "column", sm: "row" },
                }}
              >
                <PurchaseSourceSelect formik={UniqueUserpurchaseData.formik} />
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={
                    UniqueUserpurchaseData.formik.isSubmitting ? (
                      <CircularProgress size={20} />
                    ) : (
                      <DownloadIcon />
                    )
                  }
                  disabled={UniqueUserpurchaseData.formik.isSubmitting}
                  size={isMobile ? "small" : "medium"}
                  sx={{
                    minWidth: { xs: "100%", sm: 140 },
                    height: 56,
                    width: { xs: "100%", sm: "auto" },
                  }}
                >
                  {UniqueUserpurchaseData.formik.isSubmitting
                    ? "Processing..."
                    : "Download"}
                </Button>
              </Box>
            </CardContent>
          </Card>
          {/* Download Data Card */}
          <Card sx={{ mb: 3, boxShadow: 1 }}>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 2,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, color: "primary.main" }}
                  >
                    App Download Reports
                  </Typography>
                  <Chip
                    label="User Analytics"
                    color="warning"
                    size="small"
                    variant="outlined"
                  />
                  <IconButton
                    size="small"
                    onClick={() => handleClickOpen("download")}
                    sx={{ color: "text.secondary" }}
                  >
                    <InfoIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Export app download and user registration data within a specific
                date range for user analytics.
              </Typography>

              <DateRangeSelector
                selectedRange={appDataData.selectedRange}
                handleRangeSelect={appDataData.handleRangeSelect}
                formik={appDataData.formik}
              />

              <Box
                component="form"
                onSubmit={appDataData.formik.handleSubmit}
                sx={{
                  display: "flex",
                  gap: 2,
                  alignItems: "center",
                  flexWrap: "wrap",
                  mt: 1,
                  flexDirection: { xs: "column", sm: "row" },
                }}
              >
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={
                    appDataData.formik.isSubmitting ? (
                      <CircularProgress size={20} />
                    ) : (
                      <DownloadIcon />
                    )
                  }
                  disabled={appDataData.formik.isSubmitting}
                  size={isMobile ? "small" : "medium"}
                  sx={{
                    minWidth: { xs: "100%", sm: 140 },
                    height: 56,
                    width: { xs: "100%", sm: "auto" },
                  }}
                >
                  {appDataData.formik.isSubmitting
                    ? "Processing..."
                    : "Download"}
                </Button>
              </Box>
            </CardContent>
          </Card>

          {/* Coupon Usage Data Card */}
          <Card sx={{ mb: 3, boxShadow: 1 }}>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 2,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, color: "primary.main" }}
                  >
                    Coupon Usage Reports
                  </Typography>
                  <Chip
                    label="Coupons"
                    color="secondary"
                    size="small"
                    variant="outlined"
                  />
                  <IconButton
                    size="small"
                    onClick={() => handleClickOpen("coupon")}
                    sx={{ color: "text.secondary" }}
                  >
                    <InfoIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Export coupon redemption activity within a specific date range.
              </Typography>

              <DateRangeSelector
                selectedRange={couponUsageData.selectedRange}
                handleRangeSelect={couponUsageData.handleRangeSelect}
                formik={couponUsageData.formik}
              />

              <Box
                component="form"
                onSubmit={couponUsageData.formik.handleSubmit}
                sx={{
                  display: "flex",
                  gap: 2,
                  alignItems: "center",
                  flexWrap: "wrap",
                  mt: 1,
                  flexDirection: { xs: "column", sm: "row" },
                }}
              >
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={
                    couponUsageData.formik.isSubmitting ? (
                      <CircularProgress size={20} />
                    ) : (
                      <DownloadIcon />
                    )
                  }
                  disabled={couponUsageData.formik.isSubmitting}
                  size={isMobile ? "small" : "medium"}
                  sx={{
                    minWidth: { xs: "100%", sm: 140 },
                    height: 56,
                    width: { xs: "100%", sm: "auto" },
                  }}
                >
                  {couponUsageData.formik.isSubmitting
                    ? "Processing..."
                    : "Download"}
                </Button>
              </Box>
            </CardContent>
          </Card>
          {/* Notifications Usage Data Card */}
          <Card sx={{ mb: 3, boxShadow: 1 }}>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 2,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, color: "primary.main" }}
                  >
                    Book Notifications Reports
                  </Typography>
                  <Chip
                    label="Notification"
                    color="info"
                    size="small"
                    variant="outlined"
                  />
                  <IconButton
                    size="small"
                    onClick={() => handleClickOpen("Notification")}
                    sx={{ color: "text.info" }}
                  >
                    <InfoIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Export Book Notifications Reports within a specific date range.
              </Typography>

              <DateRangeSelector
                selectedRange={notificationsUsageData.selectedRange}
                handleRangeSelect={notificationsUsageData.handleRangeSelect}
                formik={notificationsUsageData.formik}
              />

              <Box
                component="form"
                onSubmit={notificationsUsageData.formik.handleSubmit}
                sx={{
                  display: "flex",
                  gap: 2,
                  alignItems: "center",
                  flexWrap: "wrap",
                  mt: 1,
                  flexDirection: { xs: "column", sm: "row" },
                }}
              >
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={
                    notificationsUsageData.formik.isSubmitting ? (
                      <CircularProgress size={20} />
                    ) : (
                      <DownloadIcon />
                    )
                  }
                  disabled={notificationsUsageData.formik.isSubmitting}
                  size={isMobile ? "small" : "medium"}
                  sx={{
                    minWidth: { xs: "100%", sm: 140 },
                    height: 56,
                    width: { xs: "100%", sm: "auto" },
                  }}
                >
                  {notificationsUsageData.formik.isSubmitting
                    ? "Processing..."
                    : "Download"}
                </Button>
              </Box>
            </CardContent>
          </Card>
          {/* Info Dialog */}
          <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            aria-labelledby="info-dialog-title"
          >
            <DialogTitle sx={{ pb: 1 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography
                  variant="h6"
                  component="div"
                  sx={{ fontWeight: 600 }}
                >
                  {dialogType === "feedback"
                    ? "App Feedback Report Information"
                    : dialogType === "booksPurchase"
                      ? "Books Purchase Report Information"
                      : dialogType === "courseNotifications"
                        ? "Course Notifications Report Information"
                        : dialogType === "universityNotifications"
                          ? "University Notifications Report Information"
                          : dialogType === "dailyPurchase"
                            ? "Daily Purchase Report Information"
                            : dialogType === "purchase"
                              ? "App Purchase Report Information"
                              : dialogType === "purchase_unique"
                                ? "App Unique Purchase Report Information"
                                : dialogType === "coupon"
                                  ? "Coupons Use Report Information"
                                  : dialogType === "Notification"
                                    ? "Notifications Use Report Information"
                                    : dialogType === "Unique_user_Purchase"
                                      ? "Unique User Purchase Report Information"
                                      : dialogType === "ios_purchases"
                                        ? "IOS Purchases Report Information"
                                        : "App Download Data Information"}
                </Typography>
                <IconButton
                  onClick={handleClose}
                  size="small"
                  sx={{ color: "text.secondary" }}
                >
                  <CancelIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent>
              <DialogContentText sx={{ fontSize: "1rem", lineHeight: 1.6 }}>
                {getDialogContent()}
              </DialogContentText>
            </DialogContent>
          </Dialog>
        </Container>
      </Box>
    </>
  );
}

export default DBDataNew;
